import { useEffect, useState } from "react";
import { Save, QrCode, Banknote, CreditCard, Wallet } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const METHODS = [
  { key: "pix", label: "Pix na entrega", icon: QrCode, desc: "Usa o preço à vista do produto" },
  { key: "cash", label: "Dinheiro na entrega", icon: Banknote, desc: "Usa o preço à vista do produto" },
  { key: "debit", label: "Cartão de débito na entrega", icon: CreditCard, desc: "Escolha abaixo qual tabela de preço o débito usa" },
  { key: "credit", label: "Cartão de crédito na entrega", icon: CreditCard, desc: "Usa o preço do cartão, com parcelamento" },
];

export default function AdminPayments() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then((r) => setForm(r.data.payment || {})).catch(() => {});
  }, []);

  if (!form) return <div className="animate-pulse h-64 bg-white rounded-xl border border-slate-200" />;

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", { payment: form });
      toast.success("Formas de pagamento salvas!");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-payments" className="max-w-3xl">
      <h1 className="font-heading text-2xl font-bold text-gray-900">Formas de Pagamento</h1>
      <p className="text-sm text-gray-500 mt-1">
        Todas as modalidades são pagas na entrega — o site nunca cobra antecipadamente nem pede dados de cartão
      </p>

      <section className="mt-6 bg-white border border-slate-200 rounded-xl p-5 space-y-4">
        <h2 className="font-heading font-semibold text-gray-900">Modalidades ativas</h2>
        {METHODS.map(({ key, label, icon: Icon, desc }) => (
          <div key={key} className="flex items-center justify-between gap-4 border-b border-slate-50 pb-3">
            <div className="flex items-center gap-3">
              <div className="bg-brand-light text-brand rounded-full p-2"><Icon className="w-4 h-4" /></div>
              <div>
                <p className="text-sm font-semibold text-gray-900">{label}</p>
                <p className="text-xs text-slate-500">{desc}</p>
              </div>
            </div>
            <Switch
              checked={!!form[key]}
              onCheckedChange={(v) => setForm({ ...form, [key]: v })}
              data-testid={`payment-toggle-${key}`}
            />
          </div>
        ))}
      </section>

      <section className="mt-6 bg-white border border-slate-200 rounded-xl p-5 space-y-4">
        <h2 className="font-heading font-semibold text-gray-900">Regra do débito</h2>
        <p className="text-xs text-slate-500">
          Regra fixa da loja: Pix, dinheiro e cartão de débito, quando usados individualmente, seguem sempre o
          valor à vista. O valor do cartão de crédito vale apenas para o crédito (parcelado) e para pagamentos combinados.
        </p>
      </section>

      <section className="mt-6 bg-white border border-slate-200 rounded-xl p-5 space-y-4">
        <h2 className="font-heading font-semibold text-gray-900">Pagamento combinado</h2>
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="bg-brand-light text-brand rounded-full p-2"><Wallet className="w-4 h-4" /></div>
            <div>
              <p className="text-sm font-semibold text-gray-900">Permitir pagamento combinado</p>
              <p className="text-xs text-slate-500">
                Regra aplicada: em qualquer pagamento combinado prevalece o valor total do cartão de crédito,
                mesmo que nenhuma das partes seja no crédito. O cliente é avisado e confirma os valores antes de finalizar.
              </p>
            </div>
          </div>
          <Switch
            checked={!!form.combined_enabled}
            onCheckedChange={(v) => setForm({ ...form, combined_enabled: v })}
            data-testid="combined-toggle"
          />
        </div>
      </section>

      <Button onClick={save} disabled={saving} className="mt-6 rounded-full px-8" data-testid="payments-save">
        <Save className="w-4 h-4" /> {saving ? "Salvando..." : "Salvar Configurações"}
      </Button>
    </div>
  );
}
